/**
 * What I Bring Section - Three key strengths
 *
 * Content from: about-us-raja-content.md (Landing Page Section 3)
 */

import React from 'react';

const strengths = [
  {
    title: "I own problems end-to-end.",
    description:
      "I designed and built the offline licensing system for an enterprise data platform used by 73% of Fortune 100 companies. No requirements doc existed. I figured out the edge cases and shipped. Another example: built cross-platform MAC address identification that works on Linux, Windows, and Mac.",
  },
  {
    title: "I automate myself out of repetitive work.",
    description:
      "Security patches used to take days per vulnerability. I built an LLM-powered workflow that analyzes tickets, traces dependencies, and tells me exactly what to fix. What took days now takes hours. Same pattern at Hitachi: introduced Ansible to automate cluster setup. What took the team days became one hour.",
  },
  {
    title: "I think about how things break before I build them.",
    description:
      "Obsessive logging. Validation before code. I caught a security backdoor in the licensing design before it shipped. My philosophy: logs tell stories. When something breaks at 3am, good logs mean you fix it in minutes, not hours. That mindset saves the whole team debugging time.",
  },
];

function WhatIBring() {
  return (
    <section className="py-24 px-6 bg-slate-50 dark:bg-slate-800 transition-colors">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold font-heading text-slate-900 dark:text-slate-50 mb-6 text-center">
          What I Bring
        </h2>

        {/* Learning Velocity - Raja's Superpower */}
        <div className="mb-12 p-8 bg-white dark:bg-slate-700 rounded-xl shadow-sm">
          <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2 text-center">
            My Superpower: Learning Velocity
          </h3>
          <p className="text-slate-500 dark:text-slate-400 text-center mb-6">
            At just one company, I picked up these technical domains:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 text-sm mb-6">
            <span className="px-3 py-1.5 bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-full">Hadoop</span>
            <span className="text-slate-300 dark:text-slate-500">→</span>
            <span className="px-3 py-1.5 bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-full">Ansible</span>
            <span className="text-slate-300 dark:text-slate-500">→</span>
            <span className="px-3 py-1.5 bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-full">Docker</span>
            <span className="text-slate-300 dark:text-slate-500">→</span>
            <span className="px-3 py-1.5 bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-full">Kubernetes</span>
            <span className="text-slate-300 dark:text-slate-500">→</span>
            <span className="px-3 py-1.5 bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-full">Kafka</span>
            <span className="text-slate-300 dark:text-slate-500">→</span>
            <span className="px-3 py-1.5 bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-full">Spark</span>
            <span className="text-slate-300 dark:text-slate-500">→</span>
            <span className="px-3 py-1.5 bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-full">Licensing SDK</span>
            <span className="text-slate-300 dark:text-slate-500">→</span>
            <span className="px-3 py-1.5 bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-300 rounded-full font-medium">LLMs</span>
          </div>
          <p className="text-slate-600 dark:text-slate-300 text-center leading-relaxed max-w-2xl mx-auto">
            Self-taught on nights and weekends. Applied to real problems. Shipped to production. Tools change, but the ability to learn fast and deliver business value is the skill that actually matters.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {strengths.map((strength, index) => (
            <div
              key={index}
              className="card-hover bg-white dark:bg-slate-700 p-6 rounded-xl shadow-sm"
            >
              <h3 className="text-xl font-semibold text-sky-700 dark:text-sky-400 mb-3">
                {strength.title}
              </h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                {strength.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default WhatIBring;
